import { useActiveEcosystems } from "@/services/api/ecosystems";
import { ActiveEcosystemReturnType } from "@thrivecoin/adapter/dist/types";
import { useEffect, useMemo } from "react";
import { useGlobalLoader } from "./useGlobalLoader";
import { useLocation, useNavigate, useParams } from "react-router-dom";

export interface UseEcosystemsReturn {
  ecosystemId: number | undefined;
  selectedEcosystem: ActiveEcosystemReturnType | null;
  ecosystems: ActiveEcosystemReturnType[] | undefined;
  onEcosystemChange: (ecosystem: ActiveEcosystemReturnType) => void;
}

export const useEcosystems = (): UseEcosystemsReturn => {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: activeEcosystems = [], isLoading } = useActiveEcosystems();
  const { show, hide } = useGlobalLoader();

  const currentPath = location.pathname;

  const ecosystems = useMemo(() => activeEcosystems, [activeEcosystems]);
  const selectedEcosystem = useMemo(
    () => (slug ? (activeEcosystems.find((e) => e.slug === slug) ?? null) : null),
    [slug, activeEcosystems],
  );
  const ecosystemId = useMemo(() => selectedEcosystem?.id, [selectedEcosystem]);

  const buildNextPath = (target?: string | null) => {
    const currentPrefix = slug ? `/x/${slug}` : null;
    const targetPrefix = target ? `/x/${target}` : null;

    if (currentPrefix && targetPrefix) {
      return currentPath.startsWith(currentPrefix)
        ? currentPath.replace(currentPrefix, targetPrefix)
        : targetPrefix + currentPath;
    }

    if (!currentPrefix && targetPrefix) {
      return targetPrefix + currentPath;
    }

    if (currentPrefix && !targetPrefix) {
      if (!currentPath.startsWith(currentPrefix)) return currentPath;
      const next = currentPath.replace(currentPrefix, "");
      return next.length ? next : "/";
    }
  };

  const onEcosystemChange = (ecosystem: ActiveEcosystemReturnType) => {
    const isSelected = selectedEcosystem?.id === ecosystem.id;

    if (isSelected || ecosystem.id === 0) {
      const url = buildNextPath(null) + location.search;
      navigate(url, { replace: true });
      return;
    }

    const targetSlug = ecosystem.slug || null;
    if (!targetSlug && !slug) return;

    const url = buildNextPath(targetSlug) + location.search;
    navigate(url, { replace: true });
  };

  useEffect(() => {
    if (isLoading) {
      show();
    } else {
      hide();
    }
  }, [isLoading]);

  return { ecosystemId, selectedEcosystem, ecosystems, onEcosystemChange };
};
